import { Attributes, Span } from "@opentelemetry/api";
import { hasKey, PerformanceEntries } from "@opentelemetry/sdk-trace-web";

import CONV from "../semantic-conventions";
import { InteractionConfig } from "../types";
import { getSize } from "../utils";
import { error, log, warn } from "../utils/logger";
import { InteractionEvent, InteractionTarget } from "./types";
import { PerformanceTimingNames as PTN } from "./types";

type InteractionLogLevel = "info" | "warn" | "error";

/**
 * Helper function for starting an event on span based on {@link PerformanceEntries}
 * @param span
 * @param performanceName name of performance entry for time start
 * @param entries
 * @param ignoreZeros
 */
export function addSpanNetworkEvent(
  span: Span,
  performanceName: string,
  entries: PerformanceEntries,
  ignoreZeros = true,
): Span | undefined {
  if (
    hasKey(entries, performanceName) &&
    typeof entries[performanceName] === "number" &&
    !(ignoreZeros && entries[performanceName] === 0)
  ) {
    return span.addEvent(performanceName, entries[performanceName]);
  }

  return undefined;
}

/**
 * Helper function for adding network events and content length attributes.
 */
export function addSpanNetworkEvents(
  span: Span,
  resource: PerformanceEntries,
  ignoreNetworkEvents = false,
  ignoreZeros?: boolean,
  skipOldSemconvContentLengthAttrs?: boolean,
): void {
  if (ignoreZeros === undefined) {
    ignoreZeros = (resource as PerformanceNavigationTiming)[PTN.START_TIME] !== 0;
  }

  if (!ignoreNetworkEvents) {
    addSpanNetworkEvent(span, PTN.FETCH_START, resource, ignoreZeros);
    addSpanNetworkEvent(span, PTN.DOMAIN_LOOKUP_START, resource, ignoreZeros);
    addSpanNetworkEvent(span, PTN.DOMAIN_LOOKUP_END, resource, ignoreZeros);
    addSpanNetworkEvent(span, PTN.CONNECT_START, resource, ignoreZeros);
    addSpanNetworkEvent(span, PTN.SECURE_CONNECTION_START, resource, ignoreZeros);
    addSpanNetworkEvent(span, PTN.CONNECT_END, resource, ignoreZeros);
    addSpanNetworkEvent(span, PTN.REQUEST_START, resource, ignoreZeros);
    addSpanNetworkEvent(span, PTN.RESPONSE_START, resource, ignoreZeros);
    addSpanNetworkEvent(span, PTN.RESPONSE_END, resource, ignoreZeros);
  }

  if (!skipOldSemconvContentLengthAttrs) {
    // This block adds content-length-related span attributes using the
    // *old* HTTP semconv (v1.7.0).
    const encodedLength = resource[PTN.ENCODED_BODY_SIZE];
    if (encodedLength !== undefined) {
      span.setAttribute(CONV.HTTP_RESPONSE_CONTENT_LENGTH, encodedLength);
      span.setAttribute(CONV.SIZE, getSize(encodedLength));
    }

    const decodedLength = resource[PTN.DECODED_BODY_SIZE];
    // Spec: Not set if transport encoding not used (in which case encoded and decoded sizes match)
    if (decodedLength !== undefined && encodedLength !== decodedLength) {
      span.setAttribute(CONV.HTTP_RESPONSE_CONTENT_LENGTH_UNCOMPRESSED, decodedLength);
    }
  }
}

export const emitInteractionLog = (
  level: InteractionLogLevel,
  message: string,
  attributes: Record<string, unknown> = {},
  err?: unknown,
) => {
  const payload = {
    message,
    attributes: {
      "l9rum.view.component": "UserInteractionInstrumentation",
      ...attributes,
    },
    ...(err ? { error: err } : {}),
  };

  if (level === "warn") {
    warn(payload);
    return;
  }

  if (level === "error") {
    error(payload);
    return;
  }

  log(payload);
};

export const extractElementInfo = (
  element: Element,
  config: InteractionConfig,
): InteractionTarget => {
  // Guard against non-elements (e.g., Document) that won't have a usable tagName
  const tagName = element?.tagName && typeof element.tagName === "string"
    ? element.tagName.toLowerCase()
    : undefined;
  if (!tagName) {
    return { tagName: "unknown" };
  }

  const target: InteractionTarget = {
    tagName,
  };

  const getAttribute = (attr: string): string | undefined => {
    if (typeof (element as Element).getAttribute !== "function") return undefined;
    const value = element.getAttribute(attr);
    return value ?? undefined;
  };

  const isFormLikeTag = ["input", "textarea", "select", "button", "option"].includes(tagName);

  if (element.id) {
    target.id = element.id;
  }

  if (element.className && typeof element.className === "string") {
    target.className = element.className.trim();
  }

  target.selector = generateSelector(element, config.selectorDepth);

  const role = getAttribute("role");
  if (role) {
    target.role = role;
  }

  const title = getAttribute("title");
  if (title) {
    target.title = title;
  }

  const ariaLabel = getAttribute("aria-label");
  if (ariaLabel) {
    target.ariaLabel = ariaLabel;
  }

  const ariaLabelledby = getAttribute("aria-labelledby");
  if (ariaLabelledby) {
    target.ariaLabelledby = ariaLabelledby;
  }

  const ariaDescribedby = getAttribute("aria-describedby");
  if (ariaDescribedby) {
    target.ariaDescribedby = ariaDescribedby;
  }

  const dataAttributes = [
    "data-testid",
    "data-test",
    "data-qa",
    "data-automation-id",
    "data-tracking-id",
    "data-l9-id",
    "data-component",
    "data-role",
  ].reduce<Record<string, string>>((acc, attr) => {
    const value = getAttribute(attr);
    if (value) {
      acc[attr] = value;
    }
    return acc;
  }, {});

  if (Object.keys(dataAttributes).length > 0) {
    target.dataAttributes = dataAttributes;
  }

  if (isFormLikeTag) {
    const formControl = element as HTMLElement & {
      name?: string;
      type?: string;
      placeholder?: string;
      labels?: ArrayLike<Element & { textContent?: string | null; htmlFor?: string }>;
      closest?: (selectors: string) => Element | null;
    };

    const controlName = formControl.name || getAttribute("name");
    if (controlName) {
      target.name = controlName;
    }

    const controlType = formControl.type || getAttribute("type");
    if (controlType) {
      target.type = controlType;
    }

    const placeholder = formControl.placeholder || getAttribute("placeholder");
    if (placeholder) {
      target.placeholder = placeholder;
    }

    const labels = formControl.labels;
    const label = labels && labels.length > 0 ? labels[0] : undefined;

    if (label) {
      const labelText = label.textContent?.trim()?.replace(/\s+/g, " ");
      if (labelText) {
        target.labelText = labelText;
      }

      const htmlFor = (label as HTMLLabelElement).htmlFor ||
        (typeof label.getAttribute === "function" ? label.getAttribute("for") ?? undefined : undefined);
      if (htmlFor) {
        target.htmlFor = htmlFor;
      }
    } else if (typeof formControl.closest === "function") {
      const closestLabel = formControl.closest("label");
      if (closestLabel) {
        const labelText = closestLabel.textContent?.trim()?.replace(/\s+/g, " ");
        if (labelText) {
          target.labelText = labelText;
        }

        const htmlFor = closestLabel.getAttribute?.("for") ?? undefined;
        if (htmlFor) {
          target.htmlFor = htmlFor;
        }
      }
    }
  }

  if (config.captureElementText) {
    const text = getElementText(element, config.elementTextMaxLength);
    if (text) {
      target.text = text;
    }
  }

  return target;
};

export const calculateScrollDepth = (): number => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return 0;
  }
  const scrollTop = window.scrollY;
  const documentHeight = document.documentElement.scrollHeight;
  const viewportHeight = window.innerHeight;
  const maxScrollTop = documentHeight - viewportHeight;

  if (maxScrollTop <= 0) return 100;

  return Math.round((scrollTop / maxScrollTop) * 100);
};

export const isSafeToTrackKey = (key: string): boolean => {
  const safeKeys = [
    "Tab",
    "Enter",
    "Escape",
    "ArrowUp",
    "ArrowDown",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End",
    "PageUp",
    "PageDown",
    "F1",
    "F2",
    "F3",
    "F4",
    "F5",
    "F6",
    "F7",
    "F8",
    "F9",
    "F10",
    "F11",
    "F12",
  ];
  return safeKeys.includes(key);
};

const matchesSensitiveIndicator = (value: string, indicators: string[]) => {
  return indicators.some((indicator) => {
    if (!value) return false;
    if (indicator === "email") {
      return (
        value === indicator ||
        value.startsWith(`${indicator}_`) ||
        value.endsWith(`_${indicator}`) ||
        value.startsWith(`${indicator}-`) ||
        value.endsWith(`-${indicator}`)
      );
    }
    return value.includes(indicator);
  });
};

const hasSensitiveDataAttribute = (element: Element): boolean => {
  const sensitiveDataAttributes = [
    "data-sensitive",
    "data-private",
    "data-secure",
    "data-l9-sensitive",
  ];
  return sensitiveDataAttributes.some((attr) => {
    const attrValue = element.getAttribute(attr);
    return element.hasAttribute(attr) && (attrValue === "" || attrValue?.toLowerCase() === "true");
  });
};

export const isSensitiveInput = (input: HTMLInputElement | HTMLTextAreaElement): boolean => {
  const sensitiveTypes = ["password", "email", "tel", "credit-card", "cc-number", "ssn"];
  const inputType = input.type?.toLowerCase() || "";
  const inputName = input.name?.toLowerCase() || "";
  const inputId = input.id?.toLowerCase() || "";
  const autocomplete = input.autocomplete?.toLowerCase() || "";
  const ariaLabel = (input.getAttribute("aria-label") || "").toLowerCase();

  const sensitiveAutocomplete = ["cc-number", "cc-csc", "cc-exp", "new-password", "current-password"];

  if (sensitiveTypes.some((type) => inputType === type)) {
    return true;
  }

  if (sensitiveAutocomplete.some((value) => autocomplete.includes(value))) {
    return true;
  }

  const sensitiveIndicators = [
    "password",
    "passcode",
    "secret",
    "token",
    "auth",
    "key",
    "credit",
    "card",
    "cc",
    "cvc",
    "cvv",
    "ssn",
    "social",
    "pin",
    "dob",
    "phone",
    "email",
  ];

  if (
    matchesSensitiveIndicator(inputName, sensitiveIndicators) ||
    matchesSensitiveIndicator(inputId, sensitiveIndicators) ||
    matchesSensitiveIndicator(ariaLabel, sensitiveIndicators)
  ) {
    return true;
  }

  if (hasSensitiveDataAttribute(input)) {
    return true;
  }

  return false;
};

export const setInteractionAttributes = (span: Span, interaction: InteractionEvent) => {
  const attrs: Attributes = {
    [CONV.INTERACTION_TYPE]: interaction.type,
    [CONV.INTERACTION_TIMESTAMP]: interaction.timestamp,
  };

  if (interaction.target) {
    if (interaction.target.tagName) {
      attrs[CONV.INTERACTION_TARGET_TAG_NAME] = interaction.target.tagName;
    }
    if (interaction.target.id) {
      attrs[CONV.INTERACTION_TARGET_ID] = interaction.target.id;
    }
    if (interaction.target.className) {
      attrs[CONV.INTERACTION_TARGET_CLASS_NAME] = interaction.target.className;
    }
    if (interaction.target.selector) {
      attrs[CONV.INTERACTION_TARGET_SELECTOR] = interaction.target.selector;
    }
    if (interaction.target.text) {
      attrs[CONV.INTERACTION_TARGET_TEXT] = interaction.target.text;
    }
    if (interaction.target.role) {
      attrs[CONV.INTERACTION_TARGET_ROLE] = interaction.target.role;
    }
    if (interaction.target.name) {
      attrs[CONV.INTERACTION_TARGET_NAME] = interaction.target.name;
    }
    if (interaction.target.type) {
      attrs[CONV.INTERACTION_TARGET_TYPE] = interaction.target.type;
    }
    if (interaction.target.placeholder) {
      attrs[CONV.INTERACTION_TARGET_PLACEHOLDER] = interaction.target.placeholder;
    }
    if (interaction.target.ariaLabel) {
      attrs[CONV.INTERACTION_TARGET_ARIA_LABEL] = interaction.target.ariaLabel;
    }
    if (interaction.target.ariaLabelledby) {
      attrs[CONV.INTERACTION_TARGET_ARIA_LABELLEDBY] = interaction.target.ariaLabelledby;
    }
    if (interaction.target.ariaDescribedby) {
      attrs[CONV.INTERACTION_TARGET_ARIA_DESCRIBEDBY] = interaction.target.ariaDescribedby;
    }
    if (interaction.target.title) {
      attrs[CONV.INTERACTION_TARGET_TITLE] = interaction.target.title;
    }
    if (interaction.target.htmlFor) {
      attrs[CONV.INTERACTION_TARGET_HTML_FOR] = interaction.target.htmlFor;
    }
    if (interaction.target.labelText) {
      attrs[CONV.INTERACTION_TARGET_LABEL_TEXT] = interaction.target.labelText;
    }
    if (interaction.target.dataAttributes) {
      Object.entries(interaction.target.dataAttributes).forEach(([key, value]) => {
        attrs[`interaction.target.data.${key.replace(/^data-/, "")}`] = value;
      });
    }
  }

  if (interaction.position) {
    attrs[CONV.INTERACTION_POSITION_X] = interaction.position.x;
    attrs[CONV.INTERACTION_POSITION_Y] = interaction.position.y;
  }

  if (interaction.metadata) {
    Object.entries(interaction.metadata).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        attrs[`interaction.${key}`] = String(value);
      }
    });
  }

  span.setAttributes(attrs);
};

const generateSelector = (element: Element, selectorDepth = 3): string => {
  let current: Element | null = element;
  const parts: string[] = [];
  let depthRemaining = Math.max(selectorDepth || 1, 1);

  while (current && depthRemaining > 0) {
    const selector = buildSelectorSegment(current);
    parts.unshift(selector);

    if (current.id) {
      break;
    }

    current = current.parentElement;
    depthRemaining -= 1;
  }

  return parts.join(" > ");
};

const buildSelectorSegment = (element: Element): string => {
  if (element.id) {
    return `#${element.id}`;
  }

  if (element.className && typeof element.className === "string") {
    const classes = element.className.trim().split(/\s+/).filter(Boolean).slice(0, 3);
    if (classes.length > 0) {
      return `${element.tagName.toLowerCase()}.${classes.join(".")}`;
    }
  }

  return element.tagName.toLowerCase();
};

const getElementText = (element: Element, elementTextMaxLength?: number): string | undefined => {
  const text = element.textContent?.trim().replace(/\s+/g, " ");
  if (!text) return undefined;

  const maxLength = elementTextMaxLength || 100;
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};
